import React, { useState, useEffect, useContext } from "react";
import UserLocationReadOnlyRow from "./UserLocationReadOnlyRow";
import UserLocationTableRow from "./UserLocationTableRow";
import UserContext from "../contexts/UserContext";
import { useTranslation } from 'react-i18next';
import FetchUtil from "../utils/FetchUtil";
import "../css/ListUsers.css";
import { useNavigate } from "react-router-dom";

function UserLocationTable({ key, setLocation, loginId, companyId }) {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [add, setAdd] = useState(false);
	const [addRowTop, setAddRowTop] = useState(false);
	const [addRowBottom, setAddRowBottom] = useState(false);
	const [error, setError] = useState("");
	const [isLocationLoading, setIsLocationLoading] = useState(false);
	const [isDeptLoading, setIsDeptLoading] = useState(false);
	const [userDepartments, setUserDepartments] = useState([]);
	const [userLocations, setUserLocations] = useState([]);
	const [searchValue, setSearchValue] = useState("");
	const [count, setCount] = useState(key);
	const closeText = t("page.close");
	const userCtx = useContext(UserContext);

	const initialValues = {
		loginId: loginId,
		companyId: companyId,
		locationKey: 0,
	};
	const [formValues, setFormValues] = useState(initialValues);

	const resetCounter = () => {
		setCount(prevCount => prevCount + 1);
	};

	let userCompanyId = (userCtx.role !== "SiteAdmin") ? userCtx.userCompanyId : 0;
	useEffect(() => {
		fetchUserDepartments();
		fetchUserLocations(searchValue);
	}, []);

	const handleClose = (event) => {
		setLocation(false);
	};

	function fetchUserDepartments() {
		setError("");
		if (!initialValues.companyId || initialValues.companyId === 0 || !initialValues.loginId) {
			return;
		}

		setIsDeptLoading(true);
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source: "web", getAll: false, companyId: initialValues.companyId,
				loginId: initialValues.loginId, userCompanyId: userCompanyId
			}),
		};
		try {
			FetchUtil.fetchAPI("GetUserDepartment", "/Service/UserService/GetUserDepartment", requestOptions, retrieveUserDepartmentSuccess, retrieveUserDepartmentFail);
		} catch (error) {
			setIsDeptLoading(false);
			throw new Error(error.message);
		}
	}

	function retrieveUserDepartmentFail(redirect) {
		setIsDeptLoading(false);
		if (redirect) {
			navigate("/");
		}
		setError(t("error.deptNotRetrieved"));
	}

	function retrieveUserDepartmentSuccess(responseData) {
		let fetchUserDepartmentsResponse;
		if (responseData) {
			fetchUserDepartmentsResponse = responseData.fetchUserDepartmentsResponse;
			if (fetchUserDepartmentsResponse && fetchUserDepartmentsResponse.errorCode === "0") {
				setUserDepartments(fetchUserDepartmentsResponse.departments.slice())
			} else if (fetchUserDepartmentsResponse && fetchUserDepartmentsResponse.errorCode === "114") {
				navigate("/", {
					state: {
						errorMessage: t("error.sessionTimeout"),
					},
				});
			} else if (fetchUserDepartmentsResponse) {
				setError(t("error.error") + " " + fetchUserDepartmentsResponse.errorMessage);
			} else {
				setError(t("error.system.fetch"));
			}
		}
		setIsDeptLoading(false);
	}

	function fetchUserLocations(searchValueInput) {
		setError("");
		if (!initialValues.companyId || initialValues.companyId === 0 || !initialValues.loginId) {
			return;
		}
		resetCounter();
		setIsLocationLoading(true);
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				source: "web", getAll: true,
				companyId: initialValues.companyId,
				loginId: initialValues.loginId,
				userCompanyId: userCompanyId,
				searchValue: searchValueInput,
			}),
		};
		try {
			FetchUtil.fetchAPI("GetUserLocation", "/Service/UserService/GetUserLocation", requestOptions, retrieveUserLocationSuccess, retrieveUserLocationFail);
		} catch (error) {
			setIsLocationLoading(false);
			throw new Error(error.message);
		}
	}

	function retrieveUserLocationFail(redirect) {
		setIsLocationLoading(false);
		if (redirect) {
			navigate("/");
		}
		setError(t("error.userGetLocFailed"));
	}

	function retrieveUserLocationSuccess(responseData) {
		let fetchUserLocationsResponse;
		if (responseData) {
			fetchUserLocationsResponse = responseData.fetchUserLocationsResponse;
			if (fetchUserLocationsResponse && fetchUserLocationsResponse.errorCode === "0") {
				setUserLocations(fetchUserLocationsResponse.locations.slice())
			} else if (fetchUserLocationsResponse && fetchUserLocationsResponse.errorCode === "114") {
				navigate("/", {
					state: {
						errorMessage: t("error.sessionTimeout"),
					},
				});
			} else if (fetchUserLocationsResponse) {
				setError(t("error.error") + " " + fetchUserLocationsResponse.errorMessage);
			} else {
				setError(t("error.system.fetch"));
			}
		}
		setIsLocationLoading(false);
	}

	const handleDeleteClick = (event, userLocationItem, completeCallback) => {
		event.preventDefault();
		deleteUserLocation(userLocationItem, completeCallback);
	};
	const deleteUserLocation = (userLocationItem, completeCallback) => {
		let action = 3;
		if (userLocationItem.statusCode === 1) {
			action = 4;
		}
		let serverParams = {
			loginId: formValues.loginId,
			locationKey: userLocationItem.locationKey,
			userCompanyId: userCompanyId,
			source: "web",
			action: action
		};
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(serverParams),
		};

		try {
			FetchUtil.fetchAPI("DeleteUserLocation", "/Service/UserService/DeleteUserLocation", requestOptions, deleteUserLocationSuccess, deleteUserLocationFail, completeCallback);
		} catch (error) {
			setError(error.message);
			if (completeCallback) {
				completeCallback();
			}
		}
	};

	function deleteUserLocationFail(redirect, completeCallback) {
		setError(t("error.userLocEditFailed"));
		if (completeCallback) {
			completeCallback();
		}
	}
	function deleteUserLocationSuccess(responseData, completeCallback) {
		let deleteUserLocationResponse;
		if (responseData) {
			deleteUserLocationResponse = responseData.deleteUserLocationResponse;
			if (deleteUserLocationResponse) {
				if (deleteUserLocationResponse.errorCode === "0") {
					resetRowTable();
					fetchUserLocations(searchValue);
				} else {
					setError(t("error.error") + " " +
						deleteUserLocationResponse.errorMessage
					);
				}
			}
		}
		if (completeCallback) {
			completeCallback();
		}
	}
	const addRowTableTop = () => {
		setAddRowTop(true);
		setAddRowBottom(false);
		addRowTable();
	};

	const addRowTableBottom = () => {
		setAddRowTop(false);
		setAddRowBottom(true);
		addRowTable();
	};
	const addRowTable = () => {
		setError("");
		if (!add) {
			setAdd(true);
		} else {
			setError(t("error.only1RowAllowed"));
		}

	};
	const removeRowTable = () => {
		setError("");
		resetRowTable();
	};
	const resetRowTable = () => {
		setAddRowTop(false);
		setAddRowBottom(false);
		setAdd(false);
	};
	const handleSubmit = (action, setValues, completeCallback) => {
		setError("");
		let found = false;
		if (!formValues.loginId) {
			setError(t("error.userReqd"));
			if (completeCallback) {
				completeCallback();
			}
			return false;
		}
		if (action === 1) {
			if (userLocations.length > 0) {
				userLocations.map((userLocationItem, index) => {
					if (userLocationItem.locationKey === setValues.locationKey) {
						setError(t("error.locDup"));
						found = true;
						if (completeCallback) {
							completeCallback();
						}
						return false;
					}
				})
			}
			if (!found) {
				saveUserLocation(action, setValues, completeCallback);
			}
		}
	};
	const saveUserLocation = (action, setValues, completeCallback) => {
		let serverParams = {
			loginId: formValues.loginId,
			companyId: formValues.companyId,
			locationKey: setValues.locationKey,
			userCompanyId: userCompanyId,
			source: "web",
			action: action
		};
		const requestOptions = {
			credentials: "same-origin",
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(serverParams),
		};

		try {
			FetchUtil.fetchAPI("AddUpdateUserLocation", "/Service/UserService/AddUpdateUserLocation", requestOptions, saveUserLocationSuccess, saveUserLocationFail, completeCallback);
		} catch (error) {
			setError(error.message);
			if (completeCallback) {
				completeCallback();
			}
		}
	};

	function saveUserLocationFail(redirect, completeCallback) {
		setError(t("error.userLocEditFailed"));
		if (completeCallback) {
			completeCallback();
		}
	}
	function saveUserLocationSuccess(responseData, completeCallback) {
		let addUpdateUserLocationResponse;
		if (responseData) {
			addUpdateUserLocationResponse = responseData.addUpdateUserLocationResponse;
			if (addUpdateUserLocationResponse) {
				if (addUpdateUserLocationResponse.errorCode === "0") {
					resetRowTable();
					fetchUserLocations(searchValue);
				} else {
					setError(t("error.error") + " " +
						addUpdateUserLocationResponse.errorMessage
					);
				}
			}
		}
		if (completeCallback) {
			completeCallback();
		}
	}
	const valueHandler = (e) => {
		const { name, value } = e.target;
		if (typeof value === 'undefined') {
			value = "";
		}
		setSearchValue(value);
	}
	const searchHandler = (e) => {
		fetchUserLocations(searchValue);
	}
	const onClickClear = (e) => {
		setSearchValue("");
		fetchUserLocations("");
	}
	return (
		<>
			{(userCtx.role === "SiteAdmin" || userCtx.role === "ClientAdmin") ?
				<div className="container-fluid container_list_sm viewh-85">
					<div className="row">
						<div className="col mt-3">
							{(error) ? (
								<div id="inerrorMessage" className="errorblock">
									{error}
								</div>
							) : (<></>)}
						</div>
						{(isLocationLoading || isDeptLoading) && (<div> <span class="spinner-border spinner-border-sm spinner-lg-size"></span> </div>)}
					</div>
					<div className="row" style={{ margin: "0px" }}>
						<div className="col">
							<a class="popup-close" id="boxclose" onClick={(e) => handleClose(e)} ></a>
						</div>
					</div>
					<div className="row">
						<div className="col mt-3">
							<input type="text" id="searchData" value={searchValue} placeholder={t("page.search.plc")} className="search-text" onChange={(e) => valueHandler(e)} />&nbsp;
							<button type="submit" className="btn btn-primary btn-block search-button" onClick={searchHandler} >
								<span class="fa fa-search fa-2">{' '}</span>{t("page.search")}
							</button>
							<button className="btn btn-primary btn-block search-clear" onClick={onClickClear}>{t("page.clear")}
							</button>
						</div>
					</div>
					<div className="row">
						<div className="col">
							<div id="placeholder_1" className="placeholder-pg"></div>
						</div>
					</div>
					<div className="row">
						<div className="col">
							<table className="list-table" role="table">
								<thead>
									<tr role="row">
										<th className="list-th">{t("page.dept.name")}</th>
										<th className="list-th">{t("page.loc.name")}</th>
										<th className="list-th">{t("page.address")}</th>
										<th className="list-th">{t("page.status")}</th>
										<th className="list-th" style={{ maxWidth: "9vw" }}>
											<button className="btn btn-primary btn-sm me-2 mb-1" onClick={addRowTableTop}>
												{t("page.addRow")}
											</button>&nbsp;&nbsp;
											<button className="btn btn-primary btn-sm me-2 mb-1" onClick={removeRowTable}>
												{t("page.delRow")}
											</button>
										</th>
									</tr>
								</thead>
								<tbody role="rowgroup">
									{add && addRowTop &&
										<UserLocationTableRow
											key={count}
											loginId={loginId}
											companyId={companyId}
											userDepartments={userDepartments}
											handleSubmit={handleSubmit}
											setError={setError}
											t={t}
											setAdd={setAdd}
											setIsLocationLoading={setIsLocationLoading}
											oddIdx={((userLocations.length % 2) > 0) ? true : false} />
									}
									{(userLocations.length > 0) ? (
										userLocations.map((userLocationItem, index) => (
											<UserLocationReadOnlyRow
												key={count}
												t={t}
												userLocationItem={userLocationItem}
												handleDeleteClick={handleDeleteClick}
												oddIdx={((index % 2) > 0) ? true : false}
												add={add}
												setError={setError}
												handleSubmit={handleSubmit}
											/>
										))
									) : (<></>)}
									{add && addRowBottom &&
										<UserLocationTableRow
											key={count}
											loginId={loginId}
											companyId={companyId}
											userDepartments={userDepartments}
											handleSubmit={handleSubmit}
											setError={setError}
											t={t}
											setAdd={setAdd}
											setIsLocationLoading={setIsLocationLoading}
											oddIdx={((userLocations.length % 2) > 0) ? true : false} />
									}
								</tbody>
								{(userLocations.length > 10) ? (<tfoot>
									<tr role="row">
										<th className="list-th">{t("page.dept.name")}</th>
										<th className="list-th">{t("page.loc.name")}</th>
										<th className="list-th">{t("page.address")}</th>
										<th className="list-th">{t("page.status")}</th>
										<th className="list-th" style={{ maxWidth: "9vw" }}>
											<button className="btn btn-primary btn-sm me-2 mb-1" onClick={addRowTableBottom}>
												{t("page.addRow")}
											</button>&nbsp;&nbsp;
											<button className="btn btn-primary btn-sm me-2 mb-1" onClick={removeRowTable}>
												{t("page.delRow")}
											</button>
										</th>
									</tr>
								</tfoot>
								) : (<></>)}
							</table>
							<div className="row">
								<div className="col mt-3">
									{(error) ? (
										<div id="inerrorMessage1" className="errorblock">
											{error}
										</div>
									) : (<></>)}
								</div>
							</div>
							<div className="footer">
								<input
									type="button"
									name="edit-user-loc-close"
									id="edit-user-loc-close"
									className="btn btn-secondary btn-block" data-dismiss="modal"
									onClick={(e) => handleClose(e)}
									style={{ marginTop: "10px", marginBottom: "10px" }}
									value={closeText}
								></input>
							</div>
						</div>
					</div>
				</div>
				: null}
		</>
	);
}
export default UserLocationTable;